import { useNavigate } from 'react-router-dom';
import { Heart, Target, Users, Globe, TrendingUp, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

function AboutPage() {
  const navigate = useNavigate();

  const values = [
    {
      icon: Heart,
      title: 'Collaboration Over Competition',
      description: 'We believe independent resorts grow stronger together. Our platform fosters genuine partnerships where every member benefits from the collective success.'
    },
    {
      icon: Target,
      title: 'Supporting Independent Properties',
      description: 'Large OTAs and chains dominate the market. We empower small and mid-sized resorts to compete effectively while maintaining their unique identity.'
    },
    {
      icon: Globe,
      title: 'Promoting Offbeat India',
      description: 'Beyond the tourist hotspots, India has thousands of hidden gems. We help travelers discover unique, authentic experiences across the country.'
    },
    {
      icon: Users,
      title: 'Community-Driven Growth',
      description: "Our network thrives on mutual support. Resort owners share knowledge, refer guests, and celebrate each other's success."
    }
  ];

  const milestones = [
    { year: '2021', title: 'Founded', description: 'Started with a vision to connect independent resorts' },
    { year: '2022', title: '100+ Partners', description: 'Reached our first 100 partner resorts across India' },
    { year: '2023', title: 'Pan-India Network', description: 'Expanded to all 28 states with 300+ partners' },
    { year: '2024', title: '500+ Resorts', description: 'Growing community of successful resort owners' }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-emerald-600 to-emerald-800 text-white" data-testid="about-hero">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">About Travelodges</h1>
          <p className="text-xl text-emerald-100 mb-8">
            Empowering independent resorts across India through collaboration, innovation, and community.
          </p>
        </div>
      </section>

      {/* Vision Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white" data-testid="vision-section">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Our Vision</h2>
              <p className="text-lg text-gray-700 mb-4">
                We envision an India where independent resorts, boutique hotels, and homestays thrive without being overshadowed by large OTA platforms and hotel chains.
              </p>
              <p className="text-lg text-gray-700 mb-4">
                By building a collaborative network, we help property owners increase direct bookings, reduce dependency on high-commission platforms, and showcase the incredible diversity of Indian hospitality.
              </p>
              <p className="text-lg text-gray-700">
                Every resort has a story. We help you tell yours to the right audience.
              </p>
            </div>
            <Card className="bg-gradient-to-br from-emerald-50 to-white border-2 border-emerald-200">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Our Mission</h3>
                <ul className="space-y-4">
                  <li className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-emerald-600 rounded-full mt-2 flex-shrink-0"></div>
                    <p className="text-gray-700">Connect independent resorts into a powerful referral network</p>
                  </li>
                  <li className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-emerald-600 rounded-full mt-2 flex-shrink-0"></div>
                    <p className="text-gray-700">Help travelers discover authentic, offbeat experiences</p>
                  </li>
                  <li className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-emerald-600 rounded-full mt-2 flex-shrink-0"></div>
                    <p className="text-gray-700">Reduce dependency on high-commission OTA platforms</p>
                  </li>
                  <li className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-emerald-600 rounded-full mt-2 flex-shrink-0"></div>
                    <p className="text-gray-700">Foster community-driven growth and knowledge sharing</p>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50" data-testid="values-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Core Values</h2>
            <p className="text-xl text-gray-600">The principles that guide everything we do</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {values.map((value, index) => (
              <Card key={index} className="border-2 hover:border-emerald-500 transition-all" data-testid={`value-${index}`}>
                <CardContent className="pt-8 pb-8">
                  <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mb-4">
                    <value.icon className="h-8 w-8 text-emerald-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">{value.title}</h3>
                  <p className="text-gray-600 text-lg">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Journey Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white" data-testid="journey-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Journey</h2>
            <p className="text-xl text-gray-600">Building India's largest resort network</p>
          </div>

          <div className="relative">
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-emerald-200 hidden md:block"></div>
            <div className="space-y-12">
              {milestones.map((milestone, index) => (
                <div key={index} className={`relative md:grid md:grid-cols-2 gap-8 ${index % 2 === 0 ? '' : 'md:grid-flow-dense'}`} data-testid={`milestone-${index}`}>
                  <div className={`md:text-right ${index % 2 === 0 ? '' : 'md:col-start-2'}`}>
                    <Card className="border-2 border-emerald-200 hover:border-emerald-500 transition-all">
                      <CardContent className="pt-6 pb-6">
                        <div className="text-3xl font-bold text-emerald-600 mb-2">{milestone.year}</div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">{milestone.title}</h3>
                        <p className="text-gray-600">{milestone.description}</p>
                      </CardContent>
                    </Card>
                  </div>
                  <div className={`hidden md:flex items-center justify-center ${index % 2 === 0 ? 'md:col-start-2' : 'md:col-start-1'}`}>
                    <div className="w-4 h-4 bg-emerald-600 rounded-full"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Impact Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-emerald-50" data-testid="impact-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Impact</h2>
            <p className="text-xl text-gray-600">Making a difference in Indian hospitality</p>
          </div>

          <div className="grid md:grid-cols-4 gap-8 text-center">
            <Card>
              <CardContent className="pt-8 pb-8">
                <div className="text-5xl font-bold text-emerald-600 mb-2">500+</div>
                <div className="text-gray-600 font-medium">Partner Resorts</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-8 pb-8">
                <div className="text-5xl font-bold text-emerald-600 mb-2">28</div>
                <div className="text-gray-600 font-medium">States Covered</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-8 pb-8">
                <div className="text-5xl font-bold text-emerald-600 mb-2">10K+</div>
                <div className="text-gray-600 font-medium">Monthly Referrals</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-8 pb-8">
                <div className="text-5xl font-bold text-emerald-600 mb-2">₹20Cr+</div>
                <div className="text-gray-600 font-medium">Offline Revenue Generated</div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-emerald-600 to-emerald-800" data-testid="about-cta">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Join Our Growing Community
          </h2>
          <p className="text-xl text-emerald-100 mb-10">
            Be part of India's most supportive resort network. Let's grow together.
          </p>
          <Button 
            size="lg" 
            className="bg-white text-emerald-600 hover:bg-gray-100 text-lg px-10 py-6"
            onClick={() => navigate('/join')}
          >
            Become a Partner
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>
    </div>
  );
}

export default AboutPage;